import { Badge, Card, Table, Tabs } from '@mantine/core';
import { modals } from '@mantine/modals';
import { PageTitle } from 'components/PageTitle';
import { i18nKeys, useTranslation } from 'locales';
import { EditCollaboratorForm } from 'modules/Settings/Collaborators/CollaboratorRow/EditCollaboratorForm';
import { filter } from 'remeda';
import { type User, useLoadUsers } from 'shared/hooks/use-load-users';
import {
  ActiveCollaborator,
  getActiveTableHeaders,
  getInactiveTableHeaders,
  getPendingTableHeaders,
  InactiveCollaborator,
  PendingCollaborator,
} from './CollaboratorRow';
import { InviteCollaboratorButton } from './InviteCollaboratorButton';

export const Collaborators = () => {
  const { t } = useTranslation();

  const { users } = useLoadUsers(true);

  const onClickEditUser = (user: User) => {
    modals.open({
      title: user.name.full,
      size: 'auto',
      children: <EditCollaboratorForm initialValues={user} />,
    });
  };

  const realUsers = users.filter(({ roles }) => !roles.includes('superadmin'));
  const activeUsers = filter(realUsers, user => user.isActive && user.hasAcceptedGc);
  const inactiveUsers = filter(realUsers, user => !user.isActive && user.hasAcceptedGc);
  const pendingUsers = filter(realUsers, user => !user.hasAcceptedGc);

  return (
    <>
      <PageTitle>
        {t(i18nKeys.SETTINGS.STAFF.TITLE)}
        <PageTitle.Actions>
          <InviteCollaboratorButton users={users} />
        </PageTitle.Actions>
      </PageTitle>

      <Tabs defaultValue="active">
        <Tabs.List mb="md" mt={0}>
          {activeUsers.length > 0 && (
            <Tabs.Tab value="active">{t(i18nKeys.SETTINGS.STAFF.ACTIVE)}</Tabs.Tab>
          )}
          {pendingUsers.length > 0 && (
            <Tabs.Tab
              value="pending"
              rightSection={
                <Badge size="sm" circle variant="light">
                  {pendingUsers.length}
                </Badge>
              }
            >
              {t(i18nKeys.SETTINGS.STAFF.PENDING)}
            </Tabs.Tab>
          )}
          {inactiveUsers.length > 0 && (
            <Tabs.Tab value="inactive">{t(i18nKeys.SETTINGS.STAFF.INACTIVE)}</Tabs.Tab>
          )}
        </Tabs.List>
        <Tabs.Panel value="active">
          <TableScaffold
            headers={getActiveTableHeaders(t)}
            users={activeUsers}
            Component={ActiveCollaborator}
            onClickRow={onClickEditUser}
          />
        </Tabs.Panel>
        <Tabs.Panel value="pending">
          <TableScaffold
            headers={getPendingTableHeaders(t)}
            users={pendingUsers}
            Component={PendingCollaborator}
            onClickRow={onClickEditUser}
          />
        </Tabs.Panel>
        <Tabs.Panel value="inactive">
          <TableScaffold
            headers={getInactiveTableHeaders(t)}
            users={inactiveUsers}
            Component={InactiveCollaborator}
            onClickRow={onClickEditUser}
          />
        </Tabs.Panel>
      </Tabs>
    </>
  );
};

type TableScaffoldProps = {
  headers: { title: string }[];
  users: User[];
  Component: React.ComponentType<{ user: User; onClickRow: () => void }>;
  onClickRow: (user: User) => void;
};

const TableScaffold = ({ headers, users, Component, onClickRow }: TableScaffoldProps) => {
  return (
    <Card radius="md" p={0}>
      <Table striped>
        <Table.Thead>
          <Table.Tr>
            {headers.map(({ title }) => (
              <Table.Th key={title}>{title}</Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {users.map(user => (
            <Component key={user.id} user={user} onClickRow={() => onClickRow(user)} />
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
};
